import { useAuth0 } from '@auth0/auth0-react'
import { CircularProgress } from '@mui/material'
import { IconUsersGroup } from '@tabler/icons-react'
import moment from 'moment'
import React, { MouseEvent, useEffect, useState } from 'react'
import RoundedButton from '../../../../components/ui/RoundedButton'
import { usePlannerMode } from '../../../../context/PlannerModeContext'
import TagChip from '../../../../components/ui/TagChip'
import { logEvent } from '../../../../analytics/analytics'
import { persistAnonSave } from '../../../../services/rewardsService'
import { addAnonSavedAlias, isAnonSaved } from '../../../../helpers/anon-saves'
import { buildDirectionsUrl } from '../../../../helpers/restaurant-actions'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../../store'
import { selectLogin } from '../../../../store/authSlice'
import { selectSavedRestaurantIds } from '../../../../store/profileSlice'
import {
  favoriteRestaurant,
  selectIsMatchDetailsLoading,
  selectRestaurantDetail,
  selectRestaurantMatchDescription,
} from '../../../../store/restaurantSlice'
import useRudderStackAnalytics from '../../../../useRudderAnalytics'
import { MuiBackdrop } from '../../styled/global.styled'
import {
  CoinContainer,
  CoinImage,
  RestaurantBasicInfoActiveTimeTypography,
  RestaurantBasicInfoAddressTypography,
  RestaurantBasicInfoAddressWrapper,
  RestaurantBasicInfoChipChip,
  RestaurantBasicInfoChipWrapper,
  RestaurantBasicInfoDistanceTypography,
  RestaurantBasicInfoIconMapPin,
  RestaurantBasicInfoLocationItem,
  RestaurantBasicInfoPopover,
  RestaurantBasicInfoPriceRangePerPersonTypography,
  RestaurantBasicInfoPriceRangeTypography,
  RestaurantBasicInfoPriceRangeWrapper,
  RestaurantBasicInfoTimeButton,
  RestaurantBasicInfoTimeButtonIconChevronDown,
  RestaurantBasicInfoTimeIconClockHour7,
  RestaurantBasicInfoTimeInfoItem,
  RestaurantBasicInfoTimeTodayTypography,
  RestaurantBasicInfoTimeWrapper,
  RestaurantBasicInfoTimezoneWrapper,
  RestaurantBasicInfoWrapper,
  RestaurantInfoActionContainer,
  RestaurantInfoActionDirectionsWebButton,
  RestaurantInfoActionDirectionsWebButtonIconArrowBearRight,
  RestaurantInfoActionIconArrowBearRight,
  RestaurantInfoActionIconBookmark,
  RestaurantInfoActionIconBookmarkFilled,
  RestaurantInfoActionIconCalendarCheck,
  RestaurantInfoActionIconMessage,
  RestaurantInfoActionIconPhone,
  RestaurantInfoActionReserveTableButton,
  RestaurantInfoAiMatchTypography,
  RestaurantInfoInnerContainer,
  RestaurantInfoMainContainer,
  RestaurantInfoSignUpTypography,
  RestaurantInfoTitleChip,
  RestaurantInfoTitleChipIconRosetteDiscountCheck,
  RestaurantInfoTitleChipWrapper,
  RestaurantInfoTitleTypography,
  RestaurantInfoTitleVerifiedTypography,
  RestaurantInfoTitleWrapper,
  RestaurantMatchDescriptionCard,
  RestaurantMatchDescriptionTitle,
  RestaurantMatchDescriptionText,
} from '../styled/RestaurantInfo.styled'
import HowToEarn from './HowToEarn'
import RestaurantShare from './RestaurantShare'
import MakeReservationDialog from '../../reservations/components/MakeReservationDialog'
import { restaurantTimezone } from '../../../../helpers/restaurant-radius'
import RestaurantBasicTimeZone from '../../../../components/restaurant/RestaurantBasicTimeZone'
import ChatDrawer from '../../../../components/restaurant/ChatDrawer'

interface RestaurantInfoProps {
  // Threaded from RestaurantDetail nav state (set by the recommendation carousel) for exact rank
  // attribution of reservation_click. Absent for direct-navigation / deep-link users.
  recommendationId?: string
  rankPosition?: number
  // Model version that produced this recommendation — stamped onto the high-intent accept labels
  // (save / reserve / website) so they are attributable to a model, matching the card/list surfaces.
  modelVersion?: string
}

const RestaurantInfo: React.FC<RestaurantInfoProps> = ({
  recommendationId,
  rankPosition,
  modelVersion,
}) => {
  const [analytics, isAnalyticsReady] = useRudderStackAnalytics()
  const today = moment().format('dddd')
  const { loginWithRedirect, isAuthenticated } = useAuth0()
  const { theme, isSmallScreen, isMediumScreen, dispatch } = useAppUtilities()
  const restaurant = useAppSelector(selectRestaurantDetail)?.restaurantDTO
  const recommendation = useAppSelector(selectRestaurantDetail)?.recommendation
  const saveRestaurants = useAppSelector(selectSavedRestaurantIds)
  const userDetail = useAppSelector(selectLogin)
  const matchDescription = useAppSelector(selectRestaurantMatchDescription)
  const isMatchDetailsLoading = useAppSelector(selectIsMatchDetailsLoading)
  const [staticLoader, setStaticLoader] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [mapUrl, setMapUrl] = useState<string>('')
  const [openDrawer, setOpenDrawer] = useState<boolean>(false)
  const [openMakeReservationDialog, setOpenMakeReservationDialog] =
    useState(false)
  const openPop = Boolean(anchorEl)
  const id = openPop ? 'simple-popover' : undefined

  // Planner mode. Undefined outside the provider (tests, isolated harnesses) — the action simply
  // doesn't render, so the detail page behaves exactly as before when no planner is involved.
  const planner = usePlannerMode()
  const onPlanner = planner?.isOnPlanner(restaurant?.alias) ?? false
  // The pick is keyed by alias — hide the action on the rare alias-less restaurant.
  const showPlan = !!planner && !!restaurant?.alias

  const handlePlan = (): void => {
    void planner?.togglePlannerRestaurant({
      alias: restaurant?.alias,
      restaurantId: restaurant?.restaurantId,
      name: restaurant?.name,
      latitude: restaurant?.lat,
      longitude: restaurant?.lng,
      recommendationId,
      rankPosition,
      sourceSurface: 'restaurant_detail',
    })
  }

  useEffect(() => {
    const accountSaved =
      saveRestaurants === undefined
        ? false
        : saveRestaurants.includes(restaurant?.restaurantId ?? '')
    // Anonymous saves render as saved too (device-local mirror of the backend anon record).
    setIsSaved(accountSaved || isAnonSaved(restaurant?.alias))
  }, [saveRestaurants, restaurant?.restaurantId, restaurant?.alias])

  useEffect(() => {
    const fetchMapUrl = async (): Promise<void> => {
      const url = await onMapDirections()
      setMapUrl(url ?? '')
    }
    void fetchMapUrl()
  }, [restaurant?.restaurantId])

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const login: any = async (): Promise<void> => {
    await loginWithRedirect({ authorizationParams: { screen_hint: 'signup' } })
  }

  const tagsArray =
    restaurant?.cuisine
      ?.trim()
      ?.split(',')
      ?.map((tag) => tag.trim())
      .filter((tag) => tag.length > 0) ?? []

  const addressParts = [
    restaurant?.address1,
    restaurant?.city,
    restaurant?.state,
  ]
  const address = addressParts.filter(Boolean).join(', ')

  const onActiveTime = (): string[] => {
    const data = restaurantTimezone(restaurant)?.find(
      (day) => day.day === today,
    )
    return data?.time ?? []
  }

  const onClickBookmark = async (): Promise<void> => {
    // Anonymous: the save SUCCEEDS immediately - no login gate (mirrors RestaurantCard).
    // persistAnonSave is the durable record; the points claim promotes it into a real favorite on
    // signup/login. The old sessionStorage-intent gate died in the OAuth redirect on in-app
    // WebViews, which is why anon completion collapsed.
    if (!isAuthenticated) {
      if (isAnonSaved(restaurant?.alias)) {
        return // already anon-saved; no anon unsave (the backend record is points-bearing)
      }
      logEvent('save_intent', {
        restaurant_id: restaurant?.restaurantId ?? '',
        screen_name: 'restaurant_detail',
      })
      void persistAnonSave(restaurant?.restaurantId, restaurant?.alias)
      if (restaurant?.alias) {
        addAnonSavedAlias(restaurant.alias)
      }
      setIsSaved(true)
      logEvent('restaurant_saved', {
        restaurant_id: restaurant?.restaurantId ?? '',
        recommendation_id: recommendationId,
        auth_state: 'anon',
        screen_name: 'restaurant_detail',
      })
      return
    }

    // Authenticated save: record the completion (restaurant_saved) only when this tap ADDS a save
    // (not when it un-saves), on the success path. When the view is attributable to a recommendation,
    // also label it recommendation_accepted{action:'save'} — a strong positive training signal that
    // previously was never emitted for saves.
    const wasSaved = isSaved
    setStaticLoader(true)
    const result = await dispatch(favoriteRestaurant(restaurant?.alias ?? ''))
    setStaticLoader(false)
    if (favoriteRestaurant.fulfilled.match(result) && !wasSaved) {
      logEvent('restaurant_saved', {
        auth_state: 'authed',
        restaurant_id: restaurant?.restaurantId ?? '',
        recommendation_id: recommendationId,
        screen_name: 'restaurant_detail',
      })
      if (recommendationId) {
        logEvent('recommendation_accepted', {
          recommendation_id: recommendationId,
          restaurant_id: restaurant?.restaurantId ?? '',
          rank_position: rankPosition,
          action: 'save',
          model_version: modelVersion,
          screen_name: 'restaurant_detail',
        })
      }
    }
  }

  const trackDirectionsClick = (): void => {
    logEvent('directions_click', {
      restaurant_id: restaurant?.restaurantId ?? '',
      restaurant_alias: restaurant?.alias ?? undefined,
      // Thread the recommendation the diner arrived from (nav state / durable attribution) so a
      // directions hand-off is attributable to the reco + rank, like reservation_click already is.
      recommendation_id: recommendationId,
      rank_position: rankPosition,
      screen_name: 'restaurant_detail',
    })
  }

  const onMapDirections = async (): Promise<string> => {
    // Single shared implementation (helpers/restaurant-actions) — same directions logic the
    // restaurant card uses. Prefers lat/lng, then placeId, then the address string.
    return buildDirectionsUrl({
      latitude: restaurant?.lat,
      longitude: restaurant?.lng,
      placeId: restaurant?.placeId,
      address: restaurant
        ? `${restaurant.address1 ?? ''} ${restaurant.city ?? ''}, ${
            restaurant.state ?? ''
          } ${restaurant.zipCode ?? ''}`.trim()
        : undefined,
    })
  }

  const trackClick = (): void => {
    if (analytics && isAnalyticsReady) {
      analytics?.track('RESERVATION_MADE', {
        category: 'RESTAURANT',
        eaterId: `${userDetail?.eaterDTO?.eaterId ?? ''}`,
        eaterName: `${userDetail?.eaterDTO?.firstName ?? ''} ${
          userDetail?.eaterDTO?.lastName ?? ''
        }`,
        eaterEmail: `${userDetail?.eaterDTO?.email ?? ''}`,
        restaurantAlias: `${restaurant?.alias ?? ''}`,
      })
    }

    const reservationUrl = restaurant?.reservationUrl ?? restaurant?.website
    // First-party referral audit: durable, queryable record of reservations Resactly drives to
    // restaurants (RudderStack trackClick above is best-effort only). It's a referral/intent, not
    // a confirmed booking. destination distinguishes a real reservation page from a website
    // fallback; a missing URL ('none') flags a coverage gap.
    const destination: 'reservation_page' | 'website' | 'none' =
      restaurant?.reservationUrl
        ? 'reservation_page'
        : restaurant?.website
        ? 'website'
        : 'none'
    let destinationHost: string | undefined
    try {
      destinationHost = reservationUrl
        ? new URL(reservationUrl).host
        : undefined
    } catch {
      destinationHost = undefined
    }
    logEvent('reservation_click', {
      restaurant_id: restaurant?.restaurantId ?? '',
      restaurant_alias: restaurant?.alias ?? undefined,
      destination,
      destination_host: destinationHost,
      recommendation_id: recommendationId,
      rank_position: rankPosition,
      screen_name: 'restaurant_detail',
    })
    // High-intent acceptance: when this view is attributable to a recommendation, label the
    // reserve/website hand-off as an accepted recommendation (the strongest positive label for
    // retraining) — previously recommendation_accepted only ever carried action:'click'.
    if (recommendationId && destination !== 'none') {
      logEvent('recommendation_accepted', {
        recommendation_id: recommendationId,
        restaurant_id: restaurant?.restaurantId ?? '',
        rank_position: rankPosition,
        action: destination === 'website' ? 'website' : 'reserve',
        model_version: modelVersion,
        screen_name: 'restaurant_detail',
      })
    }
    window.open(`${reservationUrl ?? ''}`, '_blank')
  }

  // const onHandleReservation = (): void => {
  //   setOpenMakeReservationDialog(true)
  //   trackClick()
  // }

  return (
    <>
      <RestaurantInfoMainContainer>
        <RestaurantInfoInnerContainer>
          <div>
            <RestaurantInfoTitleWrapper>
              <RestaurantInfoTitleTypography>
                {restaurant?.name?.toLocaleLowerCase()}
                {restaurant?.verified && (
                  <RestaurantInfoTitleChip
                    label={
                      <RestaurantInfoTitleChipWrapper>
                        <RestaurantInfoTitleChipIconRosetteDiscountCheck />
                        <RestaurantInfoTitleVerifiedTypography>
                          Verified
                        </RestaurantInfoTitleVerifiedTypography>
                      </RestaurantInfoTitleChipWrapper>
                    }
                  />
                )}
              </RestaurantInfoTitleTypography>
              {!isAuthenticated || recommendation?.yes ? (
                <TagChip
                  data={[
                    {
                      percentage: recommendation?.yes
                        ? recommendation.yes.toFixed(2)
                        : '15',
                      title: 'Match',
                    },
                  ]}
                  textBlur={!isAuthenticated}
                />
              ) : null}

              {isAuthenticated && (
                <CoinContainer>
                  <CoinImage />
                  <HowToEarn isAuthenticated />
                </CoinContainer>
              )}

              {!isAuthenticated && !isMediumScreen && (
                <>
                  <RestaurantInfoSignUpTypography onClick={login}>
                    Sign up
                  </RestaurantInfoSignUpTypography>
                  <RestaurantInfoAiMatchTypography>
                    to see your match & {'  '}
                  </RestaurantInfoAiMatchTypography>
                  <HowToEarn isAuthenticated={false} />
                </>
              )}
            </RestaurantInfoTitleWrapper>
            <RestaurantBasicInfoWrapper>
              <RestaurantBasicInfoAddressWrapper>
                <RestaurantBasicInfoLocationItem>
                  <RestaurantBasicInfoIconMapPin />
                  <RestaurantBasicInfoAddressTypography>
                    {address}
                  </RestaurantBasicInfoAddressTypography>
                </RestaurantBasicInfoLocationItem>
                {restaurant && restaurant.distance !== 0 && (
                  <RestaurantBasicInfoLocationItem>
                    <RestaurantBasicInfoDistanceTypography>
                      {restaurant.distance} miles away
                    </RestaurantBasicInfoDistanceTypography>
                  </RestaurantBasicInfoLocationItem>
                )}
              </RestaurantBasicInfoAddressWrapper>

              <RestaurantBasicInfoTimeWrapper>
                {restaurantTimezone?.length && (
                  <RestaurantBasicInfoTimezoneWrapper>
                    <RestaurantBasicInfoTimeInfoItem>
                      <RestaurantBasicInfoTimeIconClockHour7 />
                      <RestaurantBasicInfoActiveTimeTypography
                        style={{
                          color:
                            onActiveTime()[0] === 'Closed'
                              ? theme.palette.secondary.main
                              : theme.palette.grey[600],
                        }}
                      >
                        {onActiveTime()}
                      </RestaurantBasicInfoActiveTimeTypography>
                    </RestaurantBasicInfoTimeInfoItem>

                    <RestaurantBasicInfoTimeButton
                      onClick={handleClick}
                      aria-describedby={id}
                      endIcon={<RestaurantBasicInfoTimeButtonIconChevronDown />}
                    >
                      <RestaurantBasicInfoTimeTodayTypography>
                        Today
                      </RestaurantBasicInfoTimeTodayTypography>
                    </RestaurantBasicInfoTimeButton>
                  </RestaurantBasicInfoTimezoneWrapper>
                )}

                {restaurant?.priceRange && (
                  <RestaurantBasicInfoPriceRangeWrapper>
                    <RestaurantBasicInfoPriceRangeTypography>
                      {restaurant?.priceRange}
                    </RestaurantBasicInfoPriceRangeTypography>
                    <RestaurantBasicInfoPriceRangePerPersonTypography>
                      per person
                    </RestaurantBasicInfoPriceRangePerPersonTypography>
                  </RestaurantBasicInfoPriceRangeWrapper>
                )}
              </RestaurantBasicInfoTimeWrapper>

              <RestaurantBasicInfoPopover
                id={id}
                open={openPop}
                anchorEl={anchorEl}
                onClose={handleClose}
              >
                {restaurant && (
                  <RestaurantBasicTimeZone restaurant={restaurant} />
                )}
              </RestaurantBasicInfoPopover>
            </RestaurantBasicInfoWrapper>
            <RestaurantBasicInfoChipWrapper>
              {tagsArray.map((item, index) => (
                <RestaurantBasicInfoChipChip key={index} label={item} />
              ))}
            </RestaurantBasicInfoChipWrapper>
            {(isMatchDetailsLoading || matchDescription) && (
              <RestaurantMatchDescriptionCard>
                <RestaurantMatchDescriptionTitle>
                  What This Means For You
                </RestaurantMatchDescriptionTitle>
                {isMatchDetailsLoading && !matchDescription && (
                  <RestaurantMatchDescriptionText>
                    Learning how well this restaurant fits your taste…
                  </RestaurantMatchDescriptionText>
                )}
                {!isMatchDetailsLoading && matchDescription && (
                  <RestaurantMatchDescriptionText>
                    {matchDescription}
                  </RestaurantMatchDescriptionText>
                )}
                {!isMatchDetailsLoading && !matchDescription && (
                  <RestaurantMatchDescriptionText>
                    We are still learning your fit for this restaurant.
                  </RestaurantMatchDescriptionText>
                )}
              </RestaurantMatchDescriptionCard>
            )}
          </div>
        </RestaurantInfoInnerContainer>
        {/* <RestaurantInfoActionContainer> */}
        {/*  {(restaurant?.reservationUrl ?? restaurant?.website) && */}
        {/*    (isBetween200And410 ? ( */}
        {/*      <IconButton color="bordered" onClick={onHandleReservation}> */}
        {/*        <RestaurantInfoActionIconCalendarCheck */}
        {/*          color={theme.palette.grey[800]} */}
        {/*        /> */}
        {/*      </IconButton> */}
        {/*    ) : ( */}
        {/*      <RestaurantInfoActionReserveTableButton */}
        {/*        onClick={onHandleReservation} */}
        {/*        startIcon={<RestaurantInfoActionIconCalendarCheck />} */}
        {/*      > */}
        {/*        Reserve Table */}
        {/*      </RestaurantInfoActionReserveTableButton> */}
        {/*    ))} */}
        <RestaurantInfoActionContainer>
          {(restaurant?.reservationUrl ?? restaurant?.website) && (
            <RestaurantInfoActionReserveTableButton
              href={restaurant?.reservationUrl ?? restaurant?.website}
              onClick={trackClick}
              rel="noopener noreferrer"
              startIcon={<RestaurantInfoActionIconCalendarCheck />}
            >
              <span>Reserve Table</span>
            </RestaurantInfoActionReserveTableButton>
          )}

          {!isSmallScreen ? (
            <RestaurantInfoActionDirectionsWebButton
              href={mapUrl}
              onClick={trackDirectionsClick}
              rel="noopener noreferrer"
              startIcon={
                <RestaurantInfoActionDirectionsWebButtonIconArrowBearRight />
              }
            >
              {<span>Directions</span>}
            </RestaurantInfoActionDirectionsWebButton>
          ) : (
            <RoundedButton
              target="_blank"
              component="a"
              href={mapUrl}
              onClick={trackDirectionsClick}
              rel="noopener noreferrer"
              borderColor={theme.palette.grey[300]}
              diameter={37}
            >
              <RestaurantInfoActionIconArrowBearRight />
            </RoundedButton>
          )}

          {restaurant?.phone && (
            <RoundedButton
              component="a"
              href={`tel:${restaurant?.phone}`}
              borderColor={theme.palette.grey[300]}
              diameter={37}
              onClick={() => {
                logEvent('phone_call', {
                  restaurant_id: restaurant?.restaurantId ?? '',
                  restaurant_alias: restaurant?.alias ?? undefined,
                  // Attribute the call hand-off to the recommendation the diner arrived from.
                  recommendation_id: recommendationId,
                  rank_position: rankPosition,
                  screen_name: 'restaurant_detail',
                })
              }}
            >
              <RestaurantInfoActionIconPhone />
            </RoundedButton>
          )}
          {isAuthenticated && (
            <RoundedButton
              borderColor={theme.palette.grey[300]}
              diameter={37}
              onClick={() => {
                logEvent('chat_opened', {
                  restaurant_id: restaurant?.restaurantId ?? undefined,
                  screen_name: 'restaurant_detail',
                })
                setOpenDrawer(true)
              }}
            >
              <RestaurantInfoActionIconMessage />
            </RoundedButton>
          )}
          {/* Plan with friends — the same planner action the restaurant cards carry, so the
              detail page (where a diner lands to decide) isn't a dead end for curating a plan.
              Anon-safe like Save and Share: the first tap creates the planner and turns planner
              mode on, later taps add/remove. Hidden when there's no planner context or no alias
              to key the pick by. */}
          {showPlan && (
            <RoundedButton
              onClick={handlePlan}
              disabled={planner?.busy}
              aria-label={onPlanner ? 'Remove from planner' : 'Plan with friends'}
              aria-pressed={onPlanner}
              borderColor={
                onPlanner ? theme.palette.primary.main : theme.palette.grey[300]
              }
              diameter={37}
            >
              <IconUsersGroup
                size={20}
                stroke={onPlanner ? 2.25 : 1.75}
                color={
                  onPlanner
                    ? theme.palette.primary.main
                    : theme.palette.grey[700]
                }
              />
            </RoundedButton>
          )}
          {/* Save is ungated: anonymous users can tap to save; onClickBookmark captures the
              intent and routes through signup, then BaseLayout resumes the save on return. */}
          <RoundedButton
            onClick={() => {
              void onClickBookmark()
            }}
            borderColor={theme.palette.grey[300]}
            diameter={37}
          >
            {isSaved ? (
              <RestaurantInfoActionIconBookmarkFilled />
            ) : (
              <RestaurantInfoActionIconBookmark />
            )}
          </RoundedButton>
          <RestaurantShare
            restaurantId={restaurant?.restaurantId ?? ''}
            restaurantHandle={
              restaurant?.instagram
                ? `@${restaurant.instagram.replace(/^@/, '')}`
                : null
            }
            imgUrl={
              restaurant?.imageUrl && restaurant.imageUrl.length > 0
                ? restaurant.imageUrl
                : (restaurant as any)?.imageUrl &&
                  (restaurant as any).imageUrl.length > 0
                ? (restaurant as any).imageUrl
                : ''
            }
            description={restaurant?.description ?? ''}
            title={restaurant?.name ?? ''}
            cuisine={restaurant?.cuisine?.replace(/,/g, ' ') ?? ''}
            city={restaurant?.city ?? ''}
          />
        </RestaurantInfoActionContainer>
      </RestaurantInfoMainContainer>
      <MuiBackdrop open={staticLoader}>
        <CircularProgress color="inherit" />
      </MuiBackdrop>
      <ChatDrawer open={openDrawer} toggleDrawer={setOpenDrawer} />
      <MakeReservationDialog
        open={openMakeReservationDialog}
        handleClose={() => setOpenMakeReservationDialog(false)}
      />
    </>
  )
}

export default RestaurantInfo
